import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Trash2, Mail, RefreshCw, XCircle } from 'lucide-react';

export const DeleteSubscriberModal = ({
  open,
  onOpenChange,
  deleteTarget,
  deleteStep,
  setDeleteStep,
  deleteOtpCode,
  setDeleteOtpCode,
  deleteOtpSentTo,
  deleteLoading,
  onInitiateDelete,
  onConfirmDelete
}) => {
  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) {
        onOpenChange(false);
      }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            {deleteStep === 'confirm' ? 'Confirm Deletion' : 'Enter Verification Code'}
          </DialogTitle>
          <DialogDescription>
            {deleteStep === 'confirm' 
              ? 'This action requires 2FA verification. An OTP will be sent to the parent\'s email.'
              : `A verification code has been sent to ${deleteOtpSentTo}. Please enter it below.`
            }
          </DialogDescription>
        </DialogHeader>
        
        {deleteStep === 'confirm' ? (
          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800 font-medium">You are about to delete:</p>
              <p className="text-sm text-red-700 mt-1">{deleteTarget?.info}</p>
              {deleteTarget?.type === 'parent' && (
                <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                  <XCircle className="w-3 h-3" />
                  All associated children will also be deleted
                </p>
              )}
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs text-amber-800">
                <strong>Note:</strong> A verification code will be sent to the parent's email address. 
                You will need to obtain this code from the parent to proceed with deletion.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={deleteLoading}
              >
                Cancel
              </Button>
              <Button 
                variant="destructive"
                onClick={onInitiateDelete}
                disabled={deleteLoading}
                data-testid="send-delete-otp-btn"
              >
                {deleteLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail className="w-4 h-4 mr-2" />
                    Send Verification Code
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="delete-otp">Verification Code</Label>
              <Input
                id="delete-otp"
                type="text"
                placeholder="Enter 6-digit code"
                value={deleteOtpCode}
                onChange={(e) => setDeleteOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                className="text-center text-2xl tracking-widest font-mono"
                maxLength={6}
                data-testid="delete-otp-input"
              />
              <p className="text-xs text-slate-500">
                Code sent to: {deleteOtpSentTo}
              </p>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-xs text-red-700">
                <strong>Warning:</strong> This action cannot be undone. All associated data will be permanently deleted.
              </p>
            </div>
            <DialogFooter className="gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setDeleteStep('confirm');
                  setDeleteOtpCode('');
                }}
                disabled={deleteLoading}
              >
                Back
              </Button>
              <Button 
                variant="destructive"
                onClick={onConfirmDelete}
                disabled={deleteLoading || deleteOtpCode.length !== 6}
                data-testid="confirm-delete-btn"
              >
                {deleteLoading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Permanently
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DeleteSubscriberModal;
